import { Button, Modal } from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import type { RenewalRefusalCode, VehicleCheckMatch, VehicleCheckResult } from '@/types';

/**
 * B9 (e) -- "this vehicle already has a live policy".
 *
 * Shown when the vehicle duplicate check finds an Active or Upcoming policy on
 * the same registration in the caller's own book. It is a PROMPT, not a block:
 * the server never refuses a duplicate, so "Create as New Policy Anyway"
 * always proceeds.
 *
 * "Create as Renewal" is disabled -- with the reason on screen -- when the
 * server says that policy cannot be renewed (already renewed, a premium-paying
 * term, ...). A button that looked enabled and then 400'd on save would be the
 * worse experience.
 */

interface VehicleDuplicateModalProps {
  result: VehicleCheckResult;
  onRenew(policyId: string): void;
  onNewAnyway(): void;
  onClose(): void;
}

const REFUSAL_WORDS: Record<RenewalRefusalCode, string> = {
  renewal_source_not_found: 'That policy is not in your book, so it cannot be renewed from here.',
  renewal_source_other_broker: 'That policy is not in your book, so it cannot be renewed from here.',
  renewal_source_not_issued: 'That policy was never issued, so there is nothing to renew.',
  renewal_source_already_renewed: 'That policy has already been renewed by another policy.',
  renewal_source_installment:
    'That policy is paid in instalments; it is renewed by recording the next instalment, not by a new policy.',
  renewal_customer_mismatch: 'That policy belongs to a different customer.',
};

// eslint-disable-next-line react-refresh/only-export-components
export function renewalRefusalWords(code: RenewalRefusalCode | null | undefined): string {
  return (code && REFUSAL_WORDS[code]) || 'That policy cannot be renewed.';
}

/** The match the prompt is about: the one the server suggests renewing, else
 *  the latest-expiring live policy (the server orders by `-expiry_date`). */
// eslint-disable-next-line react-refresh/only-export-components
export function primaryVehicleMatch(result: VehicleCheckResult): VehicleCheckMatch | null {
  if (!result.matches.length) return null;
  return (
    result.matches.find((m) => m.id === result.suggested_renewal_policy_id) ?? result.matches[0]
  );
}

export function VehicleDuplicateModal({ result, onRenew, onNewAnyway, onClose }: VehicleDuplicateModalProps) {
  const match = primaryVehicleMatch(result);
  if (!match) return null;

  const kind = match.lifecycle_status === 'upcoming' ? 'an upcoming policy' : 'an active policy';
  const others = result.matches.length - 1;

  return (
    <Modal isOpen onClose={onClose} title="Existing policy on this vehicle">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This vehicle already has {kind} expiring {match.expiry_label}. Create this as a renewal instead?
        </p>

        <dl className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex justify-between gap-4 py-0.5">
            <dt className="text-gray-500 dark:text-gray-400">Policy</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              <PolicyNumber source={match} />
            </dd>
          </div>
          {match.customer_name && (
            <div className="flex justify-between gap-4 py-0.5">
              <dt className="text-gray-500 dark:text-gray-400">Customer</dt>
              <dd className="text-gray-900 dark:text-gray-100">{match.customer_name}</dd>
            </div>
          )}
          {match.insurer_name && (
            <div className="flex justify-between gap-4 py-0.5">
              <dt className="text-gray-500 dark:text-gray-400">Insurer</dt>
              <dd className="text-gray-900 dark:text-gray-100">{match.insurer_name}</dd>
            </div>
          )}
          {vehicleNumberRow(result.vehicle_number)}
        </dl>

        {others > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {others === 1
              ? 'One more live policy is also recorded on this vehicle.'
              : `${others} more live policies are also recorded on this vehicle.`}
          </p>
        )}

        {!match.can_renew && (
          <p role="note" className="text-sm text-amber-700 dark:text-amber-400">
            {renewalRefusalWords(match.renew_refusal)}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onNewAnyway}>
            Create as New Policy Anyway
          </Button>
          <Button type="button" disabled={!match.can_renew} onClick={() => onRenew(match.id)}>
            Create as Renewal
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function vehicleNumberRow(vehicleNumber: string) {
  if (!vehicleNumber) return null;
  return (
    <div className="flex justify-between gap-4 py-0.5">
      <dt className="text-gray-500 dark:text-gray-400">Vehicle</dt>
      <dd className="text-gray-900 dark:text-gray-100">{vehicleNumber}</dd>
    </div>
  );
}
